import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiMiniCheck as CheckIcon } from 'react-icons/hi2';
import { Button, Input, Label, Switch, Tabs, type ThemeMode } from '@knack/asterisk-react';

import { useThemesMessagingContext } from '@/pages/themes/theme-editor/MessagingContext';
import { isPreviewModeDark } from '@/pages/themes/theme-editor/utils';
import { useThemesPageContext } from '@/pages/themes/ThemesPageContext';

type BackgroundTab = {
  value: 'light' | 'dark';
  children: React.ReactNode;
};

type Gradients = {
  light: string[];
  dark: string[];
};

const backgroundGradients: Gradients = {
  light: [
    'linear-gradient(120deg, #fccb90 0%, #d57eeb 100%)',
    'linear-gradient(43deg, #4158D0 0%, #C850C0 46%, #FFCC70 100%)',
    'linear-gradient(160deg, #0093E9 0%, #80D0C7 100%)',
    'linear-gradient(62deg, #8EC5FC 0%, #E0C3FC 100%)',
    'linear-gradient(62deg, #FBAB7E 0%, #F7CE68 100%)',
    'linear-gradient(45deg, #85FFBD 0%, #FFFB7D 100%)',
    'linear-gradient(19deg, #3EECAC 0%, #EE74E1 100%)'
  ],
  dark: [
    'linear-gradient(120deg, #555555 0%, #111111 100%)',
    'linear-gradient(60deg, #29323c 0%, #485563 100%)',
    'linear-gradient(45deg, #4b6cb7 0%, #182848 100%)'
  ]
};

const defaultGradientInfo = {
  angle: '120°',
  firstColor: '#fccb90',
  secondColor: '#d57eeb'
};

function buildGradientString(firstColor: string, secondColor: string, angle: string): string {
  return `linear-gradient(${angle.replace('°', 'deg')}, ${firstColor} 0%, ${secondColor} 100%)`;
}

function extractGradientInfo(
  gradient: string
): { angle: string; firstColor: string; secondColor: string } | null {
  // Regular expression to match the angle in the gradient (assuming it's in degrees)
  const angleRegex = /(\d+deg)/;
  const colorRegex = /#([0-9a-fA-F]{3,6})\b/g;

  // Extract the angle
  const angleMatch = gradient.match(angleRegex);
  const angle = angleMatch ? angleMatch[0].replace('deg', '°') : null;

  // Find all color matches
  const colorMatches = gradient.match(colorRegex);

  if (angle && colorMatches && colorMatches.length >= 2) {
    return {
      angle,
      firstColor: colorMatches[0],
      secondColor: colorMatches[1]
    };
  }

  // Return null if the angle or less than two colors are found
  return null;
}

function ColorPicker({
  activeBackgroundTabValue,
  firstColor,
  secondColor,
  angle,
  isFirstColor,
  setColor
}: {
  activeBackgroundTabValue: BackgroundTab['value'];
  firstColor: string;
  secondColor: string;
  angle: string;
  isFirstColor?: boolean;
  setColor: (color: string) => void;
}) {
  const { setThemeField } = useThemesPageContext();

  return (
    <div className="flex gap-2">
      <input
        type="color"
        value={isFirstColor ? firstColor : secondColor}
        className="h-10 rounded-md"
        onChange={(e) => {
          setColor(e.target.value);
          setThemeField((draft) => {
            draft.background[activeBackgroundTabValue] = buildGradientString(
              isFirstColor ? e.target.value : firstColor,
              !isFirstColor ? e.target.value : secondColor,
              angle
            );
          });
        }}
      />
    </div>
  );
}

function ColorSelector({ mode }: { mode: BackgroundTab['value'] }) {
  const [t] = useTranslation();
  const { theme, setThemeField } = useThemesPageContext();
  const [selectedBackground, setSelectedBackground] = useState<string>('default');
  const [firstColor, setFirstColor] = useState<string>(defaultGradientInfo.firstColor);
  const [secondColor, setSecondColor] = useState<string>(defaultGradientInfo.secondColor);
  const [angle, setAngle] = useState<string>(defaultGradientInfo.angle);
  const updateSelectedBackground = useCallback(
    (value: string) => {
      if (value && !backgroundGradients[mode].includes(value)) {
        return 'custom';
      }
      if (!value) {
        return 'default';
      }
      return value;
    },
    [mode]
  );

  useEffect(() => {
    setSelectedBackground(updateSelectedBackground(theme.background[mode]));
    const gradientInfo = extractGradientInfo(theme.background[mode]);
    setFirstColor(gradientInfo?.firstColor || defaultGradientInfo.firstColor);
    setSecondColor(gradientInfo?.secondColor || defaultGradientInfo.secondColor);
    setAngle(gradientInfo?.angle ? gradientInfo?.angle : defaultGradientInfo.angle);
  }, [theme.background, mode, updateSelectedBackground]);

  return (
    <>
      <div className="mt-2 flex items-center gap-2">
        <Switch
          id="use-custom-background"
          checked={theme.background[mode] !== ''}
          onCheckedChange={(state) => {
            if (state) {
              setSelectedBackground('custom');
              setThemeField((draft) => {
                [draft.background[mode]] = backgroundGradients[mode];
              });
            } else {
              setSelectedBackground('default');
              setThemeField((draft) => {
                draft.background[mode] = '';
              });
            }
          }}
        />
        <Label htmlFor="use-custom-background">{t('themes.use_custom_background')}</Label>
      </div>
      {selectedBackground !== 'default' && (
        <>
          <div className="my-4 flex flex-wrap gap-2">
            {backgroundGradients[mode].map((gradient) => (
              <Button
                intent="minimal"
                key={gradient}
                type="button"
                aria-label={t('themes.background')}
                style={{ backgroundImage: gradient }}
                className="size-6 rounded-full border border-default text-white hover:text-white focus:text-white active:text-white"
                onClick={() => {
                  setThemeField((draft) => {
                    draft.background[mode] = gradient;
                  });
                  const selectedGradientInfo = extractGradientInfo(gradient);
                  setFirstColor(selectedGradientInfo?.firstColor || defaultGradientInfo.firstColor);
                  setSecondColor(
                    selectedGradientInfo?.secondColor || defaultGradientInfo.secondColor
                  );
                  setAngle(
                    selectedGradientInfo?.angle
                      ? selectedGradientInfo?.angle
                      : defaultGradientInfo.angle
                  );
                }}
              >
                {theme.background[mode] === gradient && (
                  <>
                    <Button.Icon icon={CheckIcon} />
                    <span className="sr-only">{t('themes.selected_background')}</span>
                  </>
                )}
              </Button>
            ))}
          </div>
          <div className="mt-2 flex justify-between">
            <ColorPicker
              activeBackgroundTabValue={mode}
              firstColor={firstColor}
              secondColor={secondColor}
              angle={angle}
              isFirstColor
              setColor={setFirstColor}
            />
            <Input
              value={angle}
              className="max-w-15"
              onChange={(e) => {
                setAngle(e.target.value);
                setThemeField((draft) => {
                  draft.background[mode] = buildGradientString(
                    firstColor,
                    secondColor,
                    e.target.value
                  );
                });
              }}
            />
            <ColorPicker
              activeBackgroundTabValue={mode}
              firstColor={firstColor}
              secondColor={secondColor}
              angle={angle}
              setColor={setSecondColor}
            />
          </div>
          <div
            className="mt-1 h-10 w-full rounded-lg"
            style={{ backgroundImage: theme.background[mode] }}
          />
        </>
      )}
    </>
  );
}

export function BackgroundImage() {
  const [t] = useTranslation();
  const { theme } = useThemesPageContext();
  const { sendMessageToLiveApp } = useThemesMessagingContext();
  const [activeBackgroundTabValue, setActiveBackgroundTabValue] = useState<BackgroundTab['value']>(
    isPreviewModeDark(theme.mode) ? 'dark' : 'light'
  );
  const backgroundTabs = [
    {
      children: <span>{t('themes.light_mode')}</span>,
      value: 'light'
    },
    {
      children: <span>{t('themes.dark_mode')}</span>,
      value: 'dark'
    }
  ];

  return (
    <Tabs
      defaultValue={activeBackgroundTabValue}
      onValueChange={(value) => {
        setActiveBackgroundTabValue(value as 'light' | 'dark');
        sendMessageToLiveApp({
          action: 'set-theme-preview-mode',
          mode: value as ThemeMode
        });
      }}
    >
      <Tabs.List items={backgroundTabs} shouldDisableResponsive />
      <Tabs.Content value="light">
        <ColorSelector mode="light" />
      </Tabs.Content>
      <Tabs.Content value="dark">
        <ColorSelector mode="dark" />
      </Tabs.Content>
    </Tabs>
  );
}
